import type { RetryOptions } from '../interfaces/RetryOptions';
import type { AsyncResult, AppError } from '@shared/utils';
import { err, ok } from '@shared/utils';

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxAttempts: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000,    // 10 seconds
  factor: 2,
  retryableErrors: [
    'ECONNRESET',
    'ETIMEDOUT',
    'ECONNREFUSED',
    'EPIPE',
    'EHOSTUNREACH',
    'ENETUNREACH',
    'ENOTFOUND',
    500,
    502,
    503,
    504
  ]
};

/**
 * Determines if an error should trigger a retry based on configuration
 */
function isRetryableError(error: any, retryableErrors: Array<string | number>): boolean {
  // Network errors
  if (error.code && retryableErrors.includes(error.code)) {
    return true;
  }

  // HTTP status codes
  if (error.status && retryableErrors.includes(error.status)) {
    return true;
  }

  // Database connection errors
  if (error.message && retryableErrors.some(code => 
    typeof code === 'string' && error.message.includes(code)
  )) {
    return true;
  }

  return false;
}

/**
 * @group Functions
 * @summary <blockquote>Executes a function with exponential backoff retry logic using the Result pattern.</blockquote>
 * @param fn - The async function to execute
 * @param options - Retry configuration options
 * @returns AsyncResult containing the successful result or an error if all retries fail
 * @example
 * ```typescript
 * const result = await withRetry(async () => {
 *   return await fetchData();
 * }, { maxAttempts: 5 });
 * 
 * result.match({
 *   ok: (data) => console.log('Success:', data),
 *   err: (error) => console.error('Failed after retries:', error)
 * });
 * ```
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): AsyncResult<T, AppError> {
  const config = { ...DEFAULT_OPTIONS, ...options };
  let attempt = 1;
  let delay = config.initialDelay;

  while (attempt <= config.maxAttempts) {
    try {
      const result = await fn();
      return ok(result);
    } catch (error) {
      // If this was our last attempt, or error isn't retryable, return error
      if (
        attempt === config.maxAttempts ||
        !isRetryableError(error, config.retryableErrors)
      ) {
        return err({
          errorCode: 'RETRY_FAILED',
          errorMessage: error instanceof Error ? error.message : String(error)
        });
      }

      // Log retry attempt
      console.warn(
        `Attempt ${attempt} failed for operation. Retrying in ${delay}ms...`,
        { error, attempt, delay }
      );

      // Wait for the calculated delay
      await new Promise(resolve => setTimeout(resolve, delay));

      // Calculate next delay with exponential backoff
      delay = Math.min(delay * config.factor, config.maxDelay);
      attempt++;
    }
  }

  return err({
    errorCode: 'RETRY_EXHAUSTED',
    errorMessage: 'Retry attempts exhausted'
  });
} 